/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.script.runtime

import io.peekandpoke.klang.common.SourceLocation
import io.peekandpoke.klang.script.KlangScriptEngine
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.ArrowFunctionBody
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.script.runtime.CallArgs.Companion.resolve
import kotlin.reflect.KClass

/**
 * Metadata about a registered native type
 *
 * @property kClass The Kotlin class of the native type
 * @property qualifiedName The fully qualified class name
 */
data class NativeTypeInfo(
    val kClass: KClass<*>,
    val qualifiedName: String,
)

/**
 * Wrapper for an extension method registered on a native type
 *
 * @property methodName The name of the method
 * @property receiverClass The Kotlin class this method is registered on
 * @property invoker Function that invokes the extension method with receiver, arguments, and source location
 */
data class NativeExtensionMethod(
    val methodName: String,
    val receiverClass: KClass<*>,
    val invoker: (receiver: Any, args: List<RuntimeValue>, location: SourceLocation?, engine: KlangScriptEngine) -> RuntimeValue,
    /**
     * Optional declared parameters. Drives named-arg resolution through the
     * interpreter when the user calls this extension method by name.
     * Receiver is NOT included in this list — it's supplied separately by the
     * binding logic.
     */
    val paramSpecs: List<ParamSpec>? = null,
)

/**
 * Wrapper for an extension property registered on a native type.
 *
 * Property access (`receiver.foo`) is resolved by the interpreter to the
 * [getter] which returns the current value as a [RuntimeValue]. No call
 * parentheses are required — the property reads directly.
 *
 * @property propertyName The name of the property
 * @property receiverClass The Kotlin class this property is registered on
 * @property getter Function that produces the property value from the receiver
 */
data class NativeExtensionProperty(
    val propertyName: String,
    val receiverClass: KClass<*>,
    val getter: (receiver: Any) -> RuntimeValue,
)

/**
 * Checks that at least [expected] arguments were passed. A MINIMUM check only: a surplus is caught by
 * the parameter specs in the interpreter, and a door with no parameters has none, so such a door uses
 * [checkNoArgs] instead (`expected = 0` here can never fire).
 *
 * @param fn Function name for error reporting
 * @param args Actual arguments received
 * @param expected Minimum expected argument count
 * @param location Optional source location for error reporting
 * @throws KlangScriptArgumentError if too few arguments were provided
 */
fun checkArgsSize(fn: String, args: List<RuntimeValue>, expected: Int, location: SourceLocation? = null) {
    if (args.size < expected) {
        throw KlangScriptArgumentError(
            functionName = fn,
            message = "Call to function $fn expected $expected arguments but got ${args.size}",
            expected = expected,
            actual = args.size,
            location = location,
        )
    }
}

/**
 * Refuses any argument to a function that takes none.
 *
 * The other arities are guarded by [checkArgsSize] and by the parameter specs, but a zero-parameter
 * method has no specs, so `3.14159.round(2)` used to return 3 with the `2` silently dropped: a
 * plausible number with no diagnostic, the failure class number methods exist to remove (2026-09-08).
 */
fun checkNoArgs(fn: String, args: List<RuntimeValue>, location: SourceLocation? = null) {
    if (args.isNotEmpty()) {
        throw KlangScriptArgumentError(
            functionName = fn,
            message = "Call to function $fn expected 0 arguments but got ${args.size}",
            expected = 0,
            actual = args.size,
            location = location,
        )
    }
}

/**
 * The source location a runtime value carries, or null when it carries none.
 *
 * Only literals keep a location: a [StringValue] and a [NumberValue] know where they were
 * written, every other [RuntimeValue] is computed and has nowhere to point. This is the one
 * definition of that probe. [callInfoOf] calls it for the receiver and for every argument when
 * the KSP-generated registration code builds a `CallInfo`; keeping it here rather than pasting a
 * `when` into the generated text also keeps the generated code free of the casts that the
 * receiver's own hard cast made redundant.
 */
fun sourceLocationOf(value: Any?): SourceLocation? = when (value) {
    is StringValue -> value.location
    is NumberValue -> value.location
    else -> null
}

/**
 * The [CallInfo] of one native call: the call's own location, the receiver's (null for a
 * top-level function, which passes a null [receiver]) and one entry per argument.
 *
 * The KSP-generated registration code calls this once per door call instead of spelling the
 * construction out in every closure; it allocates exactly what that inline construction did.
 */
fun callInfoOf(receiver: Any?, args: List<RuntimeValue>, loc: SourceLocation?): CallInfo = CallInfo(
    callLocation = loc,
    receiverLocation = sourceLocationOf(receiver),
    paramLocations = args.map { arg -> sourceLocationOf(arg) },
)

/**
 * An optional, nullable argument whose Kotlin default is `null`: the converted argument when the
 * caller passed one, null when not.
 *
 * Converts exactly as [convertArgToKotlin] does once its bounds check has passed ([NullValue]
 * becomes null, anything else goes through [convertToKotlin] with [loc]), so a type error has the
 * same message and location. The bounds error of [convertArgToKotlin] cannot happen here, which
 * is why no function name is needed. Deliberately without default parameters: Kotlin/JS would
 * pass a sentinel for every omitted one at every generated call site.
 */
fun <T : Any> optArg(args: List<RuntimeValue>, index: Int, cls: KClass<T>, loc: SourceLocation?): T? {
    if (index >= args.size) {
        return null
    }

    val arg = args[index]

    if (arg is NullValue) {
        return null
    }

    return arg.convertToKotlin(cls = cls, loc = loc)
}

/**
 * Convert a RuntimeValue to a Kotlin type.
 *
 * @param cls Target Kotlin class to convert to
 * @param loc Optional source location for error reporting
 * @return The converted value as the target type
 * @throws KlangScriptTypeError if conversion is not possible
 */
fun <T : Any> RuntimeValue.convertToKotlin(cls: KClass<T>, loc: SourceLocation? = null): T {
    // println("Converting ${this::class.simpleName} to ${cls.simpleName}")

    requireCallableForFunctionSlot(cls, loc)

    // A number, a boolean, an array or an object reaches a target it cannot be as a script-level type
    // error, on every platform (`kotlinValueAs`). Before 2026-10-06 the raw value passed through and only
    // the generated code's cast refused it, as an internal ClassCastException; where no cast was emitted
    // nothing did (on JS, `Ignitor.variants(1, Ign.sine())` built a tree with a number child).
    val result = when (this) {
        // Special conversion logic for numeric values
        is NumberValue -> when (cls) {
            Double::class -> value
            Float::class -> value.toFloat()
            Int::class -> value.toInt()
            Short::class -> value.toInt().toShort()
            Byte::class -> value.toInt().toByte()
            // `Any`, `Number`, `Comparable` take the Double; any other target is a script type error.
            else -> kotlinValueAs(value, cls, loc)
        }

        is BooleanValue -> kotlinValueAs(value, cls, loc)

        is FunctionValue -> {
            requireFunctionTarget(cls, loc)
            // Build the lambda in the ARITY THE SLOT DEMANDS, not the arity the script author
            // wrote: `() => 42` handed to a `(A) -> B` parameter must be a Function1, or the
            // native's cast blows up (JVM) or silently misbinds (JS). Script params the slot
            // does not supply bind null; extra Kotlin args are dropped (see callFunction).
            this.convertFunctionToKotlin(arity = ParamSpec.functionArity(cls) ?: parameters.size)
        }

        is NativeFunctionValue -> {
            requireFunctionTarget(cls, loc)
            // Same arity discipline as for script functions: a native function handed to a
            // `(A) -> B` slot must arrive as a Function1, or the cast at the call site fails.
            kotlinLambdaOfArity(ParamSpec.functionArity(cls) ?: 0) { args ->
                function(args.map { wrapAsRuntimeValue(it) }, null).value
            }
        }

        is ArrayValue -> when (cls) {
            DoubleArray::class -> elements.map { it.convertToKotlin(Double::class) }.toDoubleArray()
            FloatArray::class -> elements.map { it.convertToKotlin(Float::class) }.toFloatArray()
            IntArray::class -> elements.map { it.convertToKotlin(Int::class) }.toIntArray()
            ShortArray::class -> elements.map { it.convertToKotlin(Short::class) }.toShortArray()
            LongArray::class -> elements.map { it.convertToKotlin(Double::class).toLong() }.toLongArray()
            ByteArray::class -> elements.map { it.convertToKotlin(Byte::class) }.toByteArray()
            BooleanArray::class -> elements.map { it.convertToKotlin(Boolean::class) }.toBooleanArray()
            List::class, Collection::class, Iterable::class, Any::class -> elements.map {
                if (it is NullValue) null else it.convertToKotlin(Any::class)
            }

            Array::class -> elements.map {
                if (it is NullValue) null else it.convertToKotlin(Any::class)
            }.toTypedArray()

            else -> kotlinValueAs(value, cls, loc)
        }

        // A native function may take the ObjectValue itself, to read properties the generic
        // `value` map has already flattened — script lambdas, for one, survive only on this path.
        is ObjectValue -> when (cls) {
            ObjectValue::class -> this
            else -> kotlinValueAs(value, cls, loc)
        }

        else -> kotlinValueAs(value, cls, loc)
    }

    @Suppress("UNCHECKED_CAST")
    return result as T
}

/**
 * [kotlinValue], this runtime value's Kotlin form, when it is an instance of [cls]; otherwise the
 * script-level type error "Cannot convert <RuntimeValue type> to <target>" at [loc].
 */
private fun RuntimeValue.kotlinValueAs(kotlinValue: Any?, cls: KClass<*>, loc: SourceLocation?): Any? {
    if (cls.isInstance(kotlinValue)) {
        return kotlinValue
    }

    throw KlangScriptTypeError(
        message = "Cannot convert ${this::class.simpleName} to ${cls.simpleName}",
        operation = "parameter conversion",
        location = loc,
    )
}

/**
 * A function value may only convert to a function type (or `Any`). Without this guard a lambda
 * landing on a `Double` slot became a `ClassCastException` on the JVM and silent garbage on
 * JS (unchecked cast), instead of a script-level type error at the call site.
 */
private fun requireFunctionTarget(cls: KClass<*>, loc: SourceLocation?) {
    if (cls == Any::class || cls in ParamSpec.FUNCTION_CLASSES) {
        return
    }
    throw KlangScriptTypeError(
        message = "expected ${cls.simpleName ?: "a value"}, got a function",
        operation = "argument conversion",
        location = loc,
    )
}

/**
 * The mirror of [requireFunctionTarget]: a function slot (`FunctionN`) takes only a callable value. Without this guard
 * a number, a boolean, an array or an object on a `(A) -> B` slot passed through unconverted and failed later, as a
 * cast error deep inside the native on the JVM and as a silent wrong value on JS (found on `Ignitor.saw().through(pedal,
 * 0.5)`, review 2026-10-02), instead of a script-level type error at the call site.
 */
private fun RuntimeValue.requireCallableForFunctionSlot(cls: KClass<*>, loc: SourceLocation?) {
    if (cls !in ParamSpec.FUNCTION_CLASSES || isCallableValue()) {
        return
    }
    val got = when (this) {
        is NumberValue -> "a number"
        is StringValue -> "a string"
        is BooleanValue -> "a boolean"
        is ArrayValue -> "an array"
        is ObjectValue -> "an object"
        is NullValue -> "null"
        is NativeObjectValue<*> -> kClass.simpleName ?: "a native object"
        is BoundNativeMethod -> "the method '$methodName' itself; call it inside a lambda (`x => ...`)"
        else -> this::class.simpleName ?: "a value"
    }
    throw KlangScriptTypeError(
        message = "expected a function, got $got",
        operation = "argument conversion",
        location = loc,
    )
}

/**
 * Convert a script [FunctionValue] to a Kotlin lambda of the given [arity].
 *
 * Supports arities 0 to 10. [arity] defaults to the script function's own parameter count;
 * a caller that knows the target slot (`FunctionN::class`) passes that arity so the lambda
 * matches the cast site regardless of how many parameters the script author declared.
 * Parameters the slot does not supply bind null (see [callFunction]).
 *
 * @return A Kotlin lambda that invokes this script function
 * @throws KlangScriptTypeError if the arity exceeds 10
 */
fun <T : Any> FunctionValue.convertFunctionToKotlin(arity: Int = parameters.size): T {
    @Suppress("UNCHECKED_CAST")
    return kotlinLambdaOfArity(arity) { args -> callFunction(args) } as T
}

/**
 * Build a `kotlin.FunctionN` of exactly [arity] parameters whose body receives the arguments as
 * a list. The ONE place that knows how to spell a Kotlin lambda per arity; both script
 * functions and native functions handed to a `FunctionN` slot go through it, so the object at
 * the native's cast site always has the arity the slot declares.
 *
 * @throws KlangScriptTypeError if [arity] exceeds 10
 */
internal fun kotlinLambdaOfArity(arity: Int, body: (List<Any?>) -> Any?): Any {
    // Each branch declares its lambda with an explicit function type: inside a `when`, a
    // bare lambda literal would take the type inferred from the first branch.
    return when (arity) {
        0 -> {
            val f: () -> Any? = { body(emptyList()) }
            f
        }
        1 -> {
            val f: (Any?) -> Any? = { a1 -> body(listOf(a1)) }
            f
        }
        2 -> {
            val f: (Any?, Any?) -> Any? = { a1, a2 -> body(listOf(a1, a2)) }
            f
        }
        3 -> {
            val f: (Any?, Any?, Any?) -> Any? = { a1, a2, a3 -> body(listOf(a1, a2, a3)) }
            f
        }
        4 -> {
            val f: (Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4 -> body(listOf(a1, a2, a3, a4)) }
            f
        }
        5 -> {
            val f: (Any?, Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4, a5 -> body(listOf(a1, a2, a3, a4, a5)) }
            f
        }
        6 -> {
            val f: (Any?, Any?, Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4, a5, a6 -> body(listOf(a1, a2, a3, a4, a5, a6)) }
            f
        }
        7 -> {
            val f: (Any?, Any?, Any?, Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4, a5, a6, a7 -> body(listOf(a1, a2, a3, a4, a5, a6, a7)) }
            f
        }
        8 -> {
            val f: (Any?, Any?, Any?, Any?, Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4, a5, a6, a7, a8 -> body(listOf(a1, a2, a3, a4, a5, a6, a7, a8)) }
            f
        }
        9 -> {
            val f: (Any?, Any?, Any?, Any?, Any?, Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4, a5, a6, a7, a8, a9 -> body(listOf(a1, a2, a3, a4, a5, a6, a7, a8, a9)) }
            f
        }
        10 -> {
            val f: (Any?, Any?, Any?, Any?, Any?, Any?, Any?, Any?, Any?, Any?) -> Any? = { a1, a2, a3, a4, a5, a6, a7, a8, a9, a10 -> body(listOf(a1, a2, a3, a4, a5, a6, a7, a8, a9, a10)) }
            f
        }
        else -> throw KlangScriptTypeError(
            message = "Cannot convert function to Kotlin. Only functions with up to 10 parameters are supported (asked for $arity).",
            operation = "parameter conversion",
            // No location available for function conversion errors
        )
    }
}

/**
 * Invoke this script function with the given raw Kotlin arguments.
 *
 * Wraps arguments as [RuntimeValue]s, creates a call environment,
 * evaluates the function body, and unwraps the result.
 *
 * @param args Raw Kotlin arguments (will be wrapped via [wrapAsRuntimeValue])
 * @return The unwrapped result of the function execution
 */
private fun FunctionValue.callFunction(args: List<Any?>): Any? {
    // 1. Wrap arguments
    val wrappedArgs = args.map { wrapAsRuntimeValue(it) }
    // 2. Create a new environment for this call, extending the closure
    val callEnv = Environment(parent = closureEnv)
    // 3. Bind the arguments to parameters. Every declared parameter is defined: a missing
    //    argument binds null. Leaving it unbound would let the name resolve through the
    //    closure to an OUTER variable of the same name, silently. Extra arguments are dropped.
    parameters.forEachIndexed { i, paramName ->
        callEnv.define(paramName, wrappedArgs.getOrElse(i) { NullValue })
    }
    // 4. Create a new Interpreter instance with a default execution context
    // This is used when script functions are called from Kotlin code
    val executionContext = ExecutionContext(sourceName = "native-callback")
    val interpreter = Interpreter(env = callEnv, engine = engine, executionContext = executionContext)
    // 5. Evaluate the body based on type
    val result = when (val functionBody = body) {
        is ArrowFunctionBody.ExpressionBody -> {
            // Expression body: implicitly return the expression value
            interpreter.evaluate(functionBody.expression)
        }

        is ArrowFunctionBody.BlockBody -> {
            // Block body: execute statements, catch return exception
            try {
                for (stmt in functionBody.statements) {
                    interpreter.executeStatement(stmt)
                }
                // If no return statement was encountered, return NullValue
                NullValue
            } catch (e: ReturnException) {
                // Return statement was encountered, return its value
                e.value
            }
        }
    }
    // 6. Unwrap the result
    return result.value
}

/**
 * Convert argument at [index] to Kotlin type. When [nullable] is true, [NullValue] returns null.
 *
 * @param fn Function name for error reporting
 * @param args List of runtime arguments
 * @param index Zero-based argument index
 * @param cls Target Kotlin class to convert to
 * @param loc Optional source location for error reporting
 * @return The converted argument value
 * @throws KlangScriptArgumentError if the argument index is out of bounds
 * @throws KlangScriptTypeError if conversion fails
 */
fun <T : Any> convertArgToKotlin(
    fn: String,
    args: List<RuntimeValue>,
    index: Int,
    cls: KClass<T>,
    nullable: Boolean = false,
    loc: SourceLocation? = null,
): T? {
    val arg = args.getOrNull(index) ?: throw KlangScriptArgumentError(
        functionName = fn,
        message = "Expected argument at index $index",
        expected = null,
        actual = null,
        location = loc,
    )

    if (nullable && arg is NullValue) {
        return null
    }

    return arg.convertToKotlin(cls = cls, loc = loc)
}

/**
 * Wrap a value as a RuntimeValue
 *
 * Automatically wraps Kotlin values in appropriate RuntimeValue types.
 * Native objects are wrapped in NativeObjectValue with KClass and qualified name.
 *
 * @param value The value to wrap (can be null, RuntimeValue, or native object)
 * @return The wrapped RuntimeValue
 */
fun wrapAsRuntimeValue(value: Any?): RuntimeValue {
    return when (value) {
        null -> NullValue
        is RuntimeValue -> value
        is String -> StringValue(value)
        is Double -> NumberValue(value)
        is Int -> NumberValue(value.toDouble())
        is Boolean -> BooleanValue(value)
        // TODO: list -> ArrayValue
        // TODO: Map -> ObjectValue
        else -> {
            // Wrap as native object
            val kClass = value::class
            val qualifiedName = kClass.simpleName ?: "Unknown"  // Use simpleName for multiplatform compatibility
            @Suppress("UNCHECKED_CAST")
            NativeObjectValue(
                kClass = kClass as KClass<Any>,
                qualifiedName = qualifiedName,
                value = value
            )
        }
    }
}

/**
 * Guard a native function call. A [KlangScriptRuntimeError] that carries a location is re-thrown as-is;
 * one without a location gets [location], the call's own (via [withLocation]), so the editor can point at
 * it. A location already there is never overwritten: the innermost call is the most precise, and a native
 * that calls back into script (a configure lambda) lets the inner call's location through.
 * Wraps any other exception in a [KlangScriptInternalError] with context.
 *
 * @param functionName Name of the native function being called
 * @param args The RuntimeValue arguments passed to the function
 * @param location Source location of the call site
 * @param block The native function body to execute
 */
inline fun guardNativeCall(
    functionName: String,
    args: List<RuntimeValue>,
    location: SourceLocation?,
    block: () -> RuntimeValue,
): RuntimeValue {
    return try {
        block()
    } catch (e: KlangScriptRuntimeError) {
        if (e.location != null || location == null) {
            throw e
        }

        throw e.withLocation(location)
    } catch (e: Throwable) {
        val argsDesc = args.mapIndexed { i, v -> "p${i + 1}=${v.toDisplayString()}" }.joinToString(", ")
        throw KlangScriptInternalError(
            message = "Internal error in native function '$functionName($argsDesc)': ${e.message ?: e::class.simpleName}",
            cause = e,
            location = location,
        )
    }
}

// ========================================================================
// Named-argument support (Phase 3)
// ========================================================================

/**
 * A single argument evaluated at a call site.
 *
 * The interpreter evaluates each AST [io.peekandpoke.klang.script.ast.Argument]
 * in source order, producing one [EvaluatedArgument] per arg. These are then
 * classified into a [CallArgs] for the all-or-nothing rule.
 */
sealed class EvaluatedArgument {
    abstract val value: RuntimeValue

    /**
     * Source location used for diagnostics that point at *this* argument —
     * e.g. the mixed-style error wants to highlight the offending positional
     * arg when it appears after a named one. Falls back to the call's own
     * location when the AST didn't carry a source location through.
     */
    abstract val location: SourceLocation?

    /** Positional: bound to the parameter at its index. */
    data class Positional(
        override val value: RuntimeValue,
        override val location: SourceLocation?,
    ) : EvaluatedArgument()

    /** Named: bound to the parameter matching [name]. */
    data class Named(
        val name: String,
        override val value: RuntimeValue,
        val nameLocation: SourceLocation?,
    ) : EvaluatedArgument() {
        override val location: SourceLocation? get() = nameLocation
    }
}

/**
 * Resolved arguments at a call site. A call is either all positional or all
 * named — never mixed. [resolve] enforces this plus "no duplicate named names".
 */
sealed class CallArgs {
    /** Number of supplied arguments, regardless of style. */
    abstract val size: Int

    /** Every argument was positional. */
    data class Positional(val values: List<RuntimeValue>) : CallArgs() {
        override val size: Int get() = values.size
    }

    /** Every argument was named. Preserves insertion order for diagnostics. */
    data class Named(val values: Map<String, RuntimeValue>) : CallArgs() {
        override val size: Int get() = values.size
    }

    /** Zero-arg call — trivially both styles. */
    data object Empty : CallArgs() {
        override val size: Int get() = 0
    }

    companion object {
        /**
         * Classify a source-ordered list of evaluated arguments into a [CallArgs].
         *
         * Throws [KlangScriptArgumentError] if:
         *  - positional and named are mixed at the same call site, OR
         *  - the same name is used twice.
         *
         * Unknown-parameter errors are raised by the callee, which owns the
         * parameter name list — not by this resolver.
         */
        fun resolve(
            functionName: String,
            evaluated: List<EvaluatedArgument>,
            callLocation: SourceLocation?,
            callStackTrace: List<CallStackFrame> = emptyList(),
        ): CallArgs {
            if (evaluated.isEmpty()) return Empty

            val firstKind = evaluated[0]::class
            val firstMismatch = evaluated.firstOrNull { it::class != firstKind }
            if (firstMismatch != null) {
                throw KlangScriptArgumentError(
                    functionName = functionName,
                    message = "Call must use either all positional or all named arguments — no mixing",
                    location = firstMismatch.location ?: callLocation,
                    callStackTrace = callStackTrace,
                )
            }

            val first = evaluated[0]
            if (first is EvaluatedArgument.Positional) {
                return Positional(evaluated.map { (it as EvaluatedArgument.Positional).value })
            }

            val map = linkedMapOf<String, RuntimeValue>()
            for (arg in evaluated) {
                arg as EvaluatedArgument.Named
                if (arg.name in map) {
                    throw KlangScriptArgumentError(
                        functionName = functionName,
                        message = "Duplicate named argument: '${arg.name}'",
                        location = arg.nameLocation ?: callLocation,
                        callStackTrace = callStackTrace,
                    )
                }
                map[arg.name] = arg.value
            }
            return Named(map)
        }
    }
}

/**
 * Canonical names of the operator methods a native object may register: internal symbols, Kotlin's
 * operator words inside double underscores, never spelled by a script. Registering an extension
 * method under one of these names makes the object take part in the corresponding KlangScript
 * syntax; the interpreter looks them up like any other extension method (supertype walk included).
 * Only [INVOKE] is wired today; the arithmetic operators of
 * KLANG-00056 (klangscript native object operators) are designed but not built.
 */
object NativeOperatorNames {
    /** `obj(a, b)`: a callable object, e.g. `Katalyst(k => k.gain(2))`; the symbol is `__invoke__`. */
    const val INVOKE = KlangScript.Invoke.NAME

    /**
     * True for an operator symbol (`__invoke__`, later `__plus__`, ...): a name of the form `__x__`. A script
     * never reaches one by member access (`perlin.__invoke__(1, 2)` is "no method"), and no suggestion or
     * completion lists one; only the operator syntax uses them.
     */
    fun isOperatorName(name: String): Boolean =
        name.length > 4 && name.startsWith("__") && name.endsWith("__")
}

/**
 * Declares one parameter of a native callable.
 *
 * @property name Parameter name used for named-arg binding.
 * @property kotlinType Kotlin class the converted value must match.
 * @property isOptional True if the script-side caller may omit this argument. An omitted
 *   optional is filled from [default] on every script call, positional or named, so a fixed
 *   optional parameter must carry a [default] (checked at construction; nothing a script author
 *   writes reaches it). The KSP processor refuses a door default it cannot turn into one.
 * @property isNullable True if the underlying Kotlin parameter type is
 *   nullable. Drives whether `NullValue` from script collapses to a Kotlin
 *   `null` (when nullable) or rejects with a type error (when not).
 * @property default The thunk that fills an omitted optional argument; null only for a required
 *   parameter or a vararg slot.
 * @property isVararg True if this slot captures trailing positional args.
 *   Vararg slots are inherently optional and need no thunk.
 */
data class ParamSpec(
    val name: String,
    val kotlinType: KClass<*>,
    val isOptional: Boolean = false,
    val isNullable: Boolean = false,
    val default: (() -> RuntimeValue)? = null,
    val isVararg: Boolean = false,
) {
    init {
        // An invariant of the registration code, never of script input: since 2026-10-06 no
        // producer emits an optional spec without a thunk, and the interpreter relies on it.
        require(!isOptional || isVararg || default != null) {
            "ParamSpec '$name' is optional but has no default thunk"
        }
    }

    /**
     * True when the Kotlin parameter is a function type (`(A) -> B`, emitted by KSP as
     * `Function1::class`). Drives the trailing-lambda rule in [ArgAlignment]: a script
     * lambda given positionally floats to the single trailing function-typed slot.
     */
    val isFunctionType: Boolean get() = kotlinType in FUNCTION_CLASSES

    companion object {
        /** `kotlin.FunctionN` for every arity a native parameter can declare; index = arity. */
        val FUNCTION_CLASSES: List<KClass<*>> = listOf(
            Function0::class, Function1::class, Function2::class, Function3::class, Function4::class,
            Function5::class, Function6::class, Function7::class, Function8::class, Function9::class,
            Function10::class,
        )

        /** The arity a `FunctionN` target class demands, or null for a non-function target. */
        fun functionArity(cls: KClass<*>): Int? = FUNCTION_CLASSES.indexOf(cls).takeIf { it >= 0 }
    }
}

/**
 * A runtime value the trailing-lambda rule treats as "a function" (see [ArgAlignment]): a script
 * function, a native function, or a Kotlin lambda returned by a native (e.g. sprudel's
 * `rev()`, a `PatternMapperFn`), which [wrapAsRuntimeValue] carries as a [NativeObjectValue].
 */
fun RuntimeValue.isCallableValue(): Boolean =
    this is FunctionValue || this is NativeFunctionValue || (this is NativeObjectValue<*> && this.value is Function<*>)

/**
 * Bind a [CallArgs] to a spec list, producing a flat List<RuntimeValue?>
 * aligned with [specs].
 *
 * Slot semantics:
 *  - Fixed required slot missing → [KlangScriptArgumentError].
 *  - Fixed optional slot missing → null at that index (caller invokes the default thunk).
 *  - Vararg slot:
 *      * Always last in [specs] (enforced at builder time).
 *      * Positional call: tail past the fixed slots is wrapped into a fresh [ArrayValue].
 *      * Named call: the named entry must already be an [ArrayValue]; it is passed through.
 *      * Missing in named call → empty [ArrayValue].
 *      * Vararg slots are never "required" (always 0+) and never use a default thunk.
 *
 * Used by Phase 4+ builder bodies and by the interpreter's
 * `positionalArgsForNative` to translate named calls into a positional list.
 */
fun resolveByParamSpec(
    functionName: String,
    specs: List<ParamSpec>,
    args: CallArgs,
    callLocation: SourceLocation?,
    callStackTrace: List<CallStackFrame> = emptyList(),
): List<RuntimeValue?> {
    val varargIdx = specs.indexOfFirst { it.isVararg }
    val hasVararg = varargIdx != -1
    val fixedCount = if (hasVararg) varargIdx else specs.size

    val result = arrayOfNulls<RuntimeValue>(specs.size)

    when (args) {
        CallArgs.Empty -> {
            // Nothing bound; required-check below catches unfilled required fixed params.
        }

        is CallArgs.Positional -> {
            if (hasVararg) {
                // First fixedCount values map to fixed slots; the remainder forms the vararg payload.
                for (i in 0 until minOf(fixedCount, args.values.size)) {
                    result[i] = args.values[i]
                }
                val tail = if (args.values.size > fixedCount) {
                    args.values.subList(fixedCount, args.values.size).toMutableList()
                } else {
                    mutableListOf()
                }
                result[varargIdx] = ArrayValue(tail)
            } else {
                if (args.values.size > specs.size) {
                    throw KlangScriptArgumentError(
                        functionName = functionName,
                        message = "too many arguments (${args.values.size}, expected ≤ ${specs.size})",
                        expected = specs.size,
                        actual = args.values.size,
                        location = callLocation,
                        callStackTrace = callStackTrace,
                    )
                }
                // Identity, except that a trailing lambda floats to the single trailing
                // function-typed slot (see ArgAlignment). Shared with the analyzer.
                val targets = ArgAlignment.positionalTargets(
                    argCount = args.values.size,
                    paramCount = specs.size,
                    isFunctionArg = { args.values[it].isCallableValue() },
                    isFunctionParam = { specs[it].isFunctionType },
                )
                args.values.forEachIndexed { i, v -> result[targets[i]] = v }
            }
        }

        is CallArgs.Named -> {
            for ((name, v) in args.values) {
                val idx = specs.indexOfFirst { it.name == name }
                if (idx == -1) {
                    throw KlangScriptArgumentError(
                        functionName = functionName,
                        message = "unknown parameter '$name' (expected: ${specs.joinToString(", ") { it.name }})",
                        location = callLocation,
                        callStackTrace = callStackTrace,
                    )
                }
                if (specs[idx].isVararg && v !is ArrayValue) {
                    throw KlangScriptArgumentError(
                        functionName = functionName,
                        message = "vararg parameter '$name' requires an array; got ${v::class.simpleName}",
                        location = callLocation,
                        callStackTrace = callStackTrace,
                    )
                }
                result[idx] = v
            }
            if (hasVararg && result[varargIdx] == null) {
                result[varargIdx] = ArrayValue(mutableListOf())
            }
        }
    }

    specs.forEachIndexed { i, spec ->
        if (result[i] == null && !spec.isOptional && !spec.isVararg) {
            throw KlangScriptArgumentError(
                functionName = functionName,
                message = "missing required parameter '${spec.name}'",
                location = callLocation,
                callStackTrace = callStackTrace,
            )
        }
    }

    return result.toList()
}
